import { useEffect, useState } from 'react';
import { Settlement } from '../../../models/Entities/Settlements/Settlement';
import { useNavigate } from 'react-router-dom';
import { useBoolean } from '@fluentui/react-hooks';
import { connect } from 'react-redux';
import { FontIcon, MessageBarType, SearchBox, Spinner } from '@fluentui/react';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import SettlementFormComponent from '../SettlementForm/Settlement.Form.Component';
import * as ComponentFunctions from './SettlementList.Functions';
import { useTranslation } from 'react-i18next';
import './SettlementList.Styles.css';
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import MonthSelectorBar from '../MonthSelectorBar/MonthSelectorBar.Component';
import { countByMonth, MonthSelection, todaySelection } from '../MonthSelectorBar/MonthSelectorBar.Functions';
import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';
import { totalsByCurrency } from './SettlementList.Totals';

// ── Status accent colors (match GetSettlementStatusClass output) ──────────
const STATUS_COLOR: Record<string, string> = {
  gray:   '#8a9ab8',
  green:  '#1a8a4a',
  blue:   '#2563eb',
  red:    '#dc2626',
  orange: '#ea580c',
  black:  '#1e293b',
  teal:   '#0d9488',   // Confirmado
  indigo: '#4338ca',   // Pagado
};

const MONTH_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

interface SettlementGroup {
  key: string;      // "2025-03" | "__nodate__"
  label: string;    // "Marzo 2025"
  sortVal: number;
  items: Settlement[];
}

const getStatusColor = (status: number | undefined): string => {
  const cls = ComponentFunctions.GetSettlementStatusClass(status).trim();
  return STATUS_COLOR[cls] ?? STATUS_COLOR.gray;
};

/** Hex color → rgba string with given alpha (0-1) */
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

/** Date → "DD/MM/YYYY" */
const fmtDate = (d: Date | string | null | undefined): string => {
  if (!d) return '';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
};

export const SettlementListComponent = (props: ComponentFunctions.Props) => {

  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [searchText, setSearchText] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<MonthSelection[]>(() => [todaySelection()]);
  const navigate = useNavigate();
  const [t] = useTranslation();

  useEffect(() => { props.GetAllSettlements(); }, []);

  // Fetch entity data only for settlements whose entity is not yet in the store
  useEffect(() => {
    if (!(props.Settlements?.length)) return;
    const neededSponsors    = new Set<number>();
    const neededPractitioners = new Set<string>();
    const neededInsurances  = new Set<number>();
    props.Settlements.forEach(s => {
      if ((s.sponsor?.length ?? 0) > 0) {
        const id = s.sponsor[0].sponsorId;
        if (!props.sponsorList?.find(sp => sp.id === id)) neededSponsors.add(id);
      } else if ((s.practitioner?.length ?? 0) > 0 && (s.protocol?.length ?? 0) > 0) {
        const id = s.practitionerId;
        if (id && !props.practitionerList?.find(p => p.userId === id)) neededPractitioners.add(id);
      } else if ((s.agreements?.length ?? 0) > 0) {
        const id = s.medicalInsuranceId;
        if (id && !props.medicalInsuranceList?.find(m => m.id === id)) neededInsurances.add(id);
      }
    });
    neededSponsors.forEach(id => props.GetSponsorById(id));
    neededPractitioners.forEach(id => props.GetPractitionerById(id));
    neededInsurances.forEach(id => props.GetMedicalInsuranceById(id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(props.Settlements ?? []).map(s => s.id).join(',')]);

  const filtered = (props.Settlements ?? []).filter(s =>
    s.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  // Cantidad por mes para las pestañas: respeta la búsqueda, igual que la lista.
  const monthCounts = countByMonth(filtered.map(s => s.settlementDateRangeInit));

  const monthFiltered = filtered.filter(s => {
    if (!s.settlementDateRangeInit) return false;
    const d = new Date(s.settlementDateRangeInit);
    return selectedMonth.some(
      sel => d.getFullYear() === sel.year && (d.getMonth() + 1) === sel.month
    );
  });

  const buildGroups = (items: Settlement[]): SettlementGroup[] => {
    const map: Record<string, SettlementGroup> = {};
    items.forEach((s) => {
      const d = s.settlementDateRangeInit ? new Date(s.settlementDateRangeInit) : null;
      const key    = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : '__nodate__';
      const label  = d ? `${MONTH_ES[d.getMonth()]} ${d.getFullYear()}` : 'Sin fecha';
      const sortVal = d ? d.getTime() : 0;
      if (!map[key]) map[key] = { key, label, sortVal, items: [] };
      map[key].items.push(s);
    });
    return Object.values(map).sort((a, b) => {
      if (a.key === '__nodate__') return 1;
      if (b.key === '__nodate__') return -1;
      return b.sortVal - a.sortVal; // más reciente primero
    });
  };

  return (
    <div className="sl-root">

      {/* ── Controls ────────────────────────────────────────────────────── */}
      <div className="sl-controls" style={{ position: 'sticky', top: 0, zIndex: 20 }}>
        <SearchBox
          className="sl-search"
          placeholder={t('settlementListComponent.searchBox.search')}
          underlined={false}
          onChange={(_, v) => setSearchText(v ?? '')}
          onClear={() => setSearchText('')}
        />
      </div>

      {/* ── Month selector ───────────────────────────────────────────── */}
      <MonthSelectorBar selected={selectedMonth} onChange={setSelectedMonth} counts={monthCounts} />

      {/* ── Error ──────────────────────────────────────────────────────── */}
      {props.failOnLoading && (
        <MessageComponent
          message={t('settlementListComponent.errorMessage.message')}
          subMessage={props.error?.ErrorMessage || ''}
          type={MessageBarType.error}
          itemList={props.error?.Errors || []}
        />
      )}

      {/* ── Loading ────────────────────────────────────────────────────── */}
      {props.isLoading && (
        <div className="sl-loading">
          <Spinner
            label={t('settlementListComponent.loading.loadingSettlements')}
            ariaLive="assertive"
            labelPosition="top"
          />
        </div>
      )}

      {/* ── Grouped card list ────────────────────────────────────────────── */}
      {!props.isLoading && monthFiltered.length > 0 && (
        <div className="sl-list" key={selectedMonth.map(s => `${s.year}-${s.month}`).join(',')}>
          {buildGroups(monthFiltered).map((group) => {
            return (
              <div key={group.key} className="sl-group">

                {/* Month header */}
                {(() => {
                  const [monthPart, yearPart] = group.label.split(' ');
                  const groupTotals = totalsByCurrency(group.items);
                  return (
                    <div className="sl-group-header">
                      <div className="sl-group-header-left">
                        <div className="sl-group-calendar">
                          <FontIcon iconName="Calendar" className="sl-group-cal-icon" />
                        </div>
                        <div className="sl-group-title-block">
                          <div className="sl-group-period">
                            <span className="sl-group-month">{monthPart}</span>
                            <span className="sl-group-year">{yearPart}</span>
                          </div>
                          <div className="sl-group-stats">
                            <span className="sl-group-count">
                              {group.items.length} {group.items.length === 1 ? 'liquidación' : 'liquidaciones'}
                            </span>
                            {groupTotals.map(total => (
                              <span key={total.currency} className="sl-group-total">
                                {formatCurrencyAmountWithIssue(total.amount, total.currency)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div className="sl-group-items">
                  {group.items.map((item: Settlement) => {
                      const color     = getStatusColor(item.status);
                      const accentBg  = hexToRgba(color, 0.1);
                      const amountsByCurrency = (item.totals ?? []).map(t => ({
                        currency: (t.currencyCode || '').toUpperCase(),
                        formatted: formatCurrencyAmountWithIssue(t.ownerAmountWithholdingTotal, t.currencyCode),
                      }));
                      const dateRange = (item.settlementDateRangeInit && item.settlementDateRangeEnd)
                        ? `${fmtDate(item.settlementDateRangeInit)} – ${fmtDate(item.settlementDateRangeEnd)}`
                        : null;
                      const entityInfo = (() => {
                        if ((item.sponsor?.length ?? 0) > 0) {
                          const sp = props.sponsorList?.find(s => s.id === item.sponsor[0].sponsorId);
                          const name = sp ? (sp.person?.businessName || [sp.person?.firstName, sp.person?.lastName].filter(Boolean).join(' ')) : undefined;
                          return { label: name ?? 'Sponsor', type: 'sponsor' as const };
                        }
                        if ((item.practitioner?.length ?? 0) > 0 && (item.protocol?.length ?? 0) > 0) {
                          const pr = props.practitionerList?.find(p => p.userId === item.practitionerId);
                          return { label: pr?.fullName ?? 'Médico', type: 'practitioner' as const };
                        }
                        if ((item.agreements?.length ?? 0) > 0) {
                          const mi = props.medicalInsuranceList?.find(m => m.id === item.medicalInsuranceId);
                          return { label: mi?.businessName ?? mi?.title ?? 'Obra social', type: 'agreement' as const };
                        }
                        return { label: undefined, type: undefined };
                      })();

                      return (
                        <div
                          key={item.id}
                          className="sl-card"
                          style={{ '--sl-accent': color, '--sl-accent-bg': accentBg } as React.CSSProperties}
                          onClick={() => navigate(`/settlements/${item.id}`)}
                        >
                          {/* Status icon in colored circle */}
                          <div className="sl-card-icon-col">
                            <div className="sl-card-status-dot">
                              <FontIcon
                                iconName={ComponentFunctions.GetSettlementStatusIcon(item.status)}
                                className={`sl-card-status-icon${item.status === 2 ? ' sl-spin' : ''}`}
                              />
                            </div>
                          </div>

                          {/* Name + sub info */}
                          <div className="sl-card-body">
                            <span className="sl-card-name">{item.name}</span>
                            <div className="sl-card-sub">
                              {entityInfo.type && (
                                <span className={`sl-entity-badge sl-entity-badge--${entityInfo.type}`}>
                                  {entityInfo.label}
                                </span>
                              )}
                              {item.identifier && (
                                <span className="sl-card-id">#{item.identifier}</span>
                              )}
                              {dateRange && (
                                <span className="sl-card-daterange">
                                  <FontIcon iconName="DateTimeMirrored" className="sl-card-date-icon" />
                                  {dateRange}
                                </span>
                              )}
                              {item.voucheredNumber && (
                                <span className="sl-card-voucher">· {item.voucheredNumber}</span>
                              )}
                            </div>
                          </div>

                          {/* Amount + status badge */}
                          <div className="sl-card-meta">
                            <div className="sl-card-amounts">
                              {amountsByCurrency.length > 0
                                ? amountsByCurrency.map((a, index) => (
                                    <span key={`${a.currency}-${index}`} className="sl-card-amount">{a.formatted}</span>
                                  ))
                                : <span className="sl-card-amount">—</span>
                              }
                            </div>
                            <span
                              className="sl-card-status-badge"
                              style={{ color, borderColor: color, background: accentBg }}
                            >
                              {t(ComponentFunctions.GetSettlementStatusDescripcion(item.status))}
                            </span>
                          </div>

                          {/* Action buttons */}
                          <div className="sl-card-actions" onClick={e => e.stopPropagation()}>
                            <button
                              className="sl-action-btn"
                              title="Ver detalle"
                              onClick={() => navigate(`/settlements/${item.id}`)}
                            >
                              <FontIcon iconName="View" />
                            </button>
                            <button
                              className="sl-action-btn"
                              title="Actualizar"
                              onClick={() => props.RefreshSettlementById(item.id)}
                            >
                              <FontIcon iconName="SyncOccurence" className={item.isRedreshing ? 'sl-spin' : ''} />
                            </button>
                            <ConfirmationButtonComponent
                              ButtonId={`deleteSettlement${item.id}`}
                              ButtonIconName="Delete"
                              ButtonText=""
                              ButtonCssClass="sl-action-btn sl-action-btn--danger"
                              ConfirmationButtonText={t('settlementListComponent.actions.delete.buttonConfirm')}
                              ConfirmationButtonCssClass="deleteButton"
                              CancelButtonText={t('settlementListComponent.actions.delete.buttonCancel')}
                              CancelButtonCssClass=""
                              Title={t('settlementListComponent.actions.delete.title')}
                              Description=""
                              ElementTextHeader={t('settlementListComponent.actions.delete.title') + item.name}
                              ElementTextInformation={t('settlementListComponent.actions.delete.messageOne')}
                              onConfirmation={() => props.DeleteSettlementById(item.id)}
                              onCancel={() => {}}
                              isOperationExecuting={props.isDeleting}
                              isOperationExecutedWithError={props.failOnDeleting}
                              OperationError={props.error}
                            />
                          </div>
                        </div>
                      );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────────────────────── */}
      {!props.isLoading && monthFiltered.length === 0 && (
        <div className="sl-empty">
          <FontIcon iconName="DocumentSearch" className="sl-empty-icon" />
          <p className="sl-empty-text">{t('settlementListComponent.noData.noSettlements')}</p>
        </div>
      )}

      {/* ── Form panel ──────────────────────────────────────────────────── */}
      <SettlementFormComponent
        isOpen={isOpen}
        dismissPanel={dismissPanel}
        openPanel={openPanel}
        ContractId={props.ContractId}
        PractitionerId={props.PractitionerId}
        ProtocolId={props.ProtocolId}
        MedicalAgreementId={props.MedicalAgreementId}
        MedicalInsuranceId={props.MedicalInsuranceId}
      />

    </div>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(SettlementListComponent as any);
